import { useRef, useEffect, useCallback, useState } from 'react';

import { CanvasDrawer } from '../components/ui/canvas/CanvasDrawer';

import { DEFAULT_CONFIG } from '../utils/constants';
import type { CycleData, TimelineTheme, OverlapColors } from '../types';

interface UseCanvasProps {
	videoDuration: number;
	cycles: CycleData[];
	currentCycleId: string | null;
	currentTime: number;
	theme?: TimelineTheme;
	showHandlesForIdentified?: boolean;
	isScrubbing?: boolean;
	snapTime?: number | null;
	dragPreview?: {
		cycleId: string;
		start: number;
		end: number;
		isValid: boolean;
	} | null;
	hoveredCycleId?: string | null;
	overlapColors?: OverlapColors;
	outOfRangeCycleIds?: string[];
	outOfRangeFromTime?: number;
	highlightedCycleIds?: string[];
}

const NO_OUT_OF_RANGE_CYCLES: string[] = [];
const NO_HIGHLIGHTED_CYCLES: string[] = [];

function hasSameTypeIdOverlap(targetCycle: CycleData, allCycles: CycleData[]): boolean {
	if (!targetCycle.type_id) return false;

	return allCycles.some((cycle) => {
		if (cycle.id === targetCycle.id) {
			return false;
		}

		const hasTimeOverlap = targetCycle.start < cycle.end && cycle.start < targetCycle.end;
		const isPaired = targetCycle.paired_cycle_id === cycle.id || cycle.paired_cycle_id === targetCycle.id;

		return !isPaired && hasTimeOverlap && cycle.type_id === targetCycle.type_id;
	});
}

export function useCanvas({
	videoDuration,
	cycles,
	currentCycleId,
	currentTime,
	theme,
	showHandlesForIdentified = false,
	isScrubbing = false,
	snapTime = null,
	dragPreview = null,
	hoveredCycleId = null,
	overlapColors,
	outOfRangeCycleIds = NO_OUT_OF_RANGE_CYCLES,
	outOfRangeFromTime,
	highlightedCycleIds = NO_HIGHLIGHTED_CYCLES
}: UseCanvasProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });

	const updateCanvasDimensions = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const container = canvas.parentElement;
		if (!container) return;

		const containerStyles = window.getComputedStyle(container);
		const containerPaddingLeft = parseInt(containerStyles.paddingLeft || '0', 10);
		const containerPaddingRight = parseInt(containerStyles.paddingRight || '0', 10);

		const availableWidth = container.clientWidth - containerPaddingLeft - containerPaddingRight;
		const availableHeight = container.clientHeight;

		const displayWidth = availableWidth > 0 ? availableWidth : canvas.offsetWidth;
		const displayHeight = availableHeight || canvas.offsetHeight;

		if (displayWidth !== canvasDimensions.width || displayHeight !== canvasDimensions.height) {
			setCanvasDimensions({ width: displayWidth, height: displayHeight });
		}
	}, [canvasDimensions]);

	const drawTimeline = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas || canvasDimensions.width === 0 || canvasDimensions.height === 0) {
			return;
		}

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}

		const { width: displayWidth, height: displayHeight } = canvasDimensions;

		const dpr = window.devicePixelRatio || 1;
		const scaledWidth = displayWidth * dpr;
		const scaledHeight = displayHeight * dpr;

		if (canvas.width !== scaledWidth || canvas.height !== scaledHeight) {
			canvas.width = scaledWidth;
			canvas.height = scaledHeight;
			canvas.style.width = `${displayWidth}px`;
			canvas.style.height = `${displayHeight}px`;
			ctx.scale(dpr, dpr);
		}

		const responsiveMargin = Math.min(DEFAULT_CONFIG.MARGIN, displayWidth * DEFAULT_CONFIG.MAX_WIDTH_RATIO);

		const drawer = new CanvasDrawer(
			ctx,
			displayWidth,
			displayHeight,
			videoDuration,
			theme,
			responsiveMargin,
			drawTimeline,
			overlapColors
		);
		drawer.clear();

		if (videoDuration === 0) return;

		if (outOfRangeFromTime) {
			drawer.drawOutOfRangeArea(outOfRangeFromTime);
		}

		drawer.drawRuler();
		const shouldRenderOverlapColoring = Boolean(overlapColors);
		const currentCycle = cycles.find((c) => c.id === currentCycleId) || null;
		const hasFlaggedSelection = cycles.some((cycle) => cycle.selected);
		const isHighlighted = (cycle: CycleData): boolean => cycle.selected === true || cycle.id === currentCycleId;
		const isOutOfRange = (cycle: CycleData): boolean => outOfRangeCycleIds.includes(cycle.id);
		const isHovered = (cycle: CycleData): boolean =>
			cycle.id === hoveredCycleId || highlightedCycleIds.includes(cycle.id);

		if (shouldRenderOverlapColoring) {
			drawer.drawOverlapRegions(cycles);

			cycles.forEach((cycle) => {
				if (isHighlighted(cycle)) return;
				if (!cycle.identified) {
					drawer.drawCurrentCycle(cycle, isHovered(cycle));
				}
			});
		} else {
			cycles.forEach((cycle) => {
				if (isOutOfRange(cycle)) {
					drawer.drawOutOfRangeCycle(cycle, isHighlighted(cycle), outOfRangeFromTime);
					return;
				}

				if (isHighlighted(cycle)) return;

				if (cycle.identified) {
					drawer.drawIdentifiedCycle(cycle, isHovered(cycle));
					return;
				}

				drawer.drawCurrentCycle(cycle, isHovered(cycle));
			});
		}

		function drawDragPreviewIfNeeded(): void {
			if (!dragPreview) return;

			const previewCycle: CycleData = {
				id: dragPreview.cycleId,
				start: dragPreview.start,
				end: dragPreview.end,
				identified: false
			};

			drawer.drawDragPreview(previewCycle, dragPreview.isValid);
		}

		function drawSelectedCycles(): void {
			cycles.forEach((cycle) => {
				if (!cycle.selected) return;
				if (isOutOfRange(cycle)) return;

				drawer.drawCurrentCycle(cycle, isHovered(cycle), cycle.editable === true);
			});

			drawDragPreviewIfNeeded();
		}

		function drawSingleSelection(): void {
			if (!currentCycle) return;

			if (isOutOfRange(currentCycle)) {
				drawDragPreviewIfNeeded();
				return;
			}

			const isCurrentCycleHovered = isHovered(currentCycle);

			if (currentCycle.identified && showHandlesForIdentified) {
				drawer.drawIdentifiedCycleWithHandles(currentCycle);
			} else if (currentCycle.identified && !shouldRenderOverlapColoring) {
				drawer.drawIdentifiedCycle(currentCycle, isCurrentCycleHovered);
			} else if (!currentCycle.identified) {
				drawer.drawCurrentCycle(currentCycle, isCurrentCycleHovered);
			}

			drawDragPreviewIfNeeded();
		}

		function drawSelection(): void {
			if (hasFlaggedSelection) {
				drawSelectedCycles();
				return;
			}

			drawSingleSelection();
		}

		drawSelection();

		const hasTypeOverlap = cycles.some((cycle) => hasSameTypeIdOverlap(cycle, cycles));

		if (hasTypeOverlap) {
			drawer.drawTypeOverlapErrorOverlay(cycles);
		}

		drawer.drawCurrentTimeNeedle(currentTime, currentCycle, isScrubbing);
	}, [
		canvasDimensions,
		videoDuration,
		cycles,
		currentCycleId,
		currentTime,
		theme,
		showHandlesForIdentified,
		isScrubbing,
		snapTime,
		dragPreview,
		hoveredCycleId,
		overlapColors,
		outOfRangeCycleIds,
		outOfRangeFromTime,
		highlightedCycleIds
	]);

	useEffect(() => {
		updateCanvasDimensions();
	}, [updateCanvasDimensions]);

	useEffect(() => {
		drawTimeline();
	}, [drawTimeline]);

	useEffect(() => {
		const handleResize = () => {
			updateCanvasDimensions();
		};

		window.addEventListener('resize', handleResize);

		const canvas = canvasRef.current;
		const container = canvas?.parentElement;
		let resizeObserver: ResizeObserver | null = null;

		if (container && window.ResizeObserver) {
			resizeObserver = new ResizeObserver(() => {
				updateCanvasDimensions();
			});

			resizeObserver.observe(container);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [canvasDimensions, updateCanvasDimensions]);

	return {
		canvasRef,
		drawTimeline,
		canvasDimensions
	};
}
